package com.qrkontaq.ui.admin

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.qrkontaq.core.auth.Session
import com.qrkontaq.core.config.AppConfig
import com.qrkontaq.ui.tenant.InlineIcon
import com.qrkontaq.ui.tenant.MenuIcons
import com.qrkontaq.ui.theme.Brand
import com.qrkontaq.utils.Analytics
import com.qrkontaq.utils.OAuthHelper
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.w3c.dom.events.Event
import org.w3c.fetch.RequestCredentials
import org.w3c.fetch.RequestInit
import kotlin.js.json

/** QRkontaq mark from the landing header: rounded QR corners with a blinking green dot. */
private const val LOGO_SVG =
    """<svg viewBox="0 0 32 32" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round">""" +
        """<path d="M4 10V7.5A3.5 3.5 0 0 1 7.5 4H10"/>""" +
        """<path d="M22 4h2.5A3.5 3.5 0 0 1 28 7.5V10"/>""" +
        """<path d="M28 22v2.5a3.5 3.5 0 0 1-3.5 3.5H22"/>""" +
        """<path d="M10 28H7.5A3.5 3.5 0 0 1 4 24.5V22"/>""" +
        """<g stroke="#34D399" stroke-width="1.7">""" +
        """<rect x="9.9" y="9.9" width="4.2" height="4.2" rx="1.3"/>""" +
        """<rect x="17.9" y="9.9" width="4.2" height="4.2" rx="1.3"/>""" +
        """<rect x="9.9" y="17.9" width="4.2" height="4.2" rx="1.3"/>""" +
        """<rect x="17.9" y="17.9" width="4.2" height="4.2" rx="1.3"/>""" +
        """</g>""" +
        """<g fill="#34D399" stroke="none">""" +
        """<rect x="11.35" y="11.35" width="1.3" height="1.3" rx="0.45"/>""" +
        """<rect x="19.35" y="11.35" width="1.3" height="1.3" rx="0.45"/>""" +
        """<rect x="11.35" y="19.35" width="1.3" height="1.3" rx="0.45"/>""" +
        """<rect x="19.35" y="19.35" width="1.3" height="1.3" rx="0.45"/>""" +
        """<circle class="dot" cx="16" cy="16" r="1.6"/>""" +
        """</g>""" +
        """</svg>"""

/**
 * Branded auth frame on one emerald backdrop: brand info on the left (desktop),
 * the white form card on the right. Shared by login, registration and invites.
 */
@Composable
internal fun AuthScaffold(content: @Composable () -> Unit) {
    InjectAdminStyles()
    Box(Modifier.fillMaxWidth().attrsModifier { classes("qk-auth") }) {
        // Back to the landing page, fixed to the top-left of every auth screen.
        Row(
            Modifier
                .attrsModifier { classes("qk-auth-back"); attr("role", "button") }
                .onClick {
                    if (window.history.length > 1) window.history.back()
                    else window.location.href = "/"
                },
            verticalAlignment = Alignment.CenterVertically,
        ) {
            InlineIcon(AdminIcons.ChevronLeft, 15, "#EAF3EE")
            SpanText(AdminT.authBack)
        }
        // Language switcher, fixed to the top-right of every auth screen.
        Box(
            Modifier.position(Position.Fixed).top(16.px).right(16.px).zIndex(60),
        ) {
            AdminLangDropdown(dark = true)
        }
        // Logo centered in the mobile top bar; on desktop it folds into the brand column.
        Box(Modifier.attrsModifier { classes("qk-auth-topbrand") }) {
            AuthLogo(onDark = true)
        }
        // Plain div so the CSS controls the axis (column on mobile → row on desktop);
        // a kobweb Row would hard-code flex-direction:row inline and break the reflow.
        Div(attrs = { classes("qk-auth-inner") }) {
            // Desktop: one brand column (logo + heading + features) beside the card.
            Column(Modifier.attrsModifier { classes("qk-auth-info") }) {
                Box(Modifier.margin(bottom = 20.px)) { AuthLogo(onDark = true) }
                BrandTagline()
                BrandFeatures()
            }
            // Mobile order 1: the heading above the card (logo is in the top bar).
            Column(Modifier.attrsModifier { classes("qk-auth-mobilehead") }) {
                BrandTagline()
            }
            // Order 2: the form card.
            Box(Modifier.attrsModifier { classes("qk-auth-cardwrap") }) {
                Column(
                    Modifier
                        .fillMaxWidth()
                        .padding(28.px)
                        .attrsModifier { classes("qk-auth-card", "qk-fade-in") },
                ) {
                    content()
                }
            }
            // Mobile order 3: the feature list at the bottom.
            Column(Modifier.attrsModifier { classes("qk-auth-mobilefeatures") }) {
                BrandFeatures()
            }
        }
    }
}

/** Logo lockup: the landing QR mark next to the "QRkontaq" wordmark (Unbounded). */
@Composable
internal fun AuthLogo(onDark: Boolean) {
    Row(verticalAlignment = Alignment.CenterVertically) {
        Box(
            Modifier.size(26.px).minWidth(26.px).margin(right = 10.px)
                .attrsModifier { classes("qk-logo-dot") },
            contentAlignment = Alignment.Center,
        ) {
            InlineIcon(LOGO_SVG, 26, if (onDark) "#FFFFFF" else "#1F2421")
        }
        SpanText(
            "QRkontaq",
            Modifier
                .fontSize(23.px)
                .fontWeight(FontWeight.SemiBold)
                .color(if (onDark) Brand.Surface else Brand.Charcoal)
                .attrsModifier { classes("qk-wordmark") },
        )
    }
}

/** The big tagline heading. Emerald backdrop, so white text. */
@Composable
private fun BrandTagline() {
    SpanText(
        AdminT.brandTagline,
        Modifier
            .fontSize(36.px)
            .color(Brand.Surface)
            .margin(bottom = 24.px)
            .styleModifier {
                property("font-family", "'Playfair Display', Georgia, serif")
                property("line-height", "1.12")
            },
    )
}

/** Feature bullets: the desktop brand column and the mobile below-the-form list. */
@Composable
private fun BrandFeatures() {
    BrandFeature(AdminIcons.QrCode, AdminT.brandFeature1)
    BrandFeature(AdminIcons.ChefHat, AdminT.brandFeature2)
    BrandFeature(com.qrkontaq.ui.tenant.MenuIcons.Receipt, AdminT.brandFeature4)
    BrandFeature(AdminIcons.ChartBar, AdminT.brandFeature3)
    BrandFeature(AdminIcons.Check, AdminT.brandFeature6)
}

@Composable
private fun BrandFeature(
    iconSvg: String,
    text: String,
) {
    Row(Modifier.margin(bottom = 14.px), verticalAlignment = Alignment.CenterVertically) {
        Box(
            Modifier
                .size(38.px)
                .minWidth(38.px)
                .borderRadius(11.px)
                .margin(right = 12.px)
                .styleModifier { property("background", "rgba(255,255,255,.1)") },
            contentAlignment = Alignment.Center,
        ) {
            InlineIcon(iconSvg, 18, "#7EE2BC")
        }
        SpanText(
            text,
            Modifier.fontSize(15.px).fontWeight(FontWeight.Medium)
                .color(Color.rgba(255, 255, 255, 0.9f)),
        )
    }
}

/** Card header: just the subtitle now — the logo lives in the scaffold's brand block. */
@Composable
internal fun AuthFormHeader(subtitle: String) {
    SpanText(
        subtitle,
        Modifier
            .fillMaxWidth()
            .fontSize(14.px)
            .color(Brand.TextSecondary)
            .margin(bottom = 18.px),
    )
}

private const val ADMIN_GOOGLE_BUTTON_ID = "qk-admin-google"

/** fetch credentials mode that sends/accepts the API's httpOnly session cookie. */
private val INCLUDE_CREDENTIALS = "include".unsafeCast<RequestCredentials>()

/**
 * Verifies the raw Google/Apple id token server-side and stores the admin token pair.
 * Returns isNewUser on success (so the caller can count first-time sign-ups), or null
 * on failure. Runs on the admin's own origin — no popup — so the origin must be an
 * authorized origin in the Google client / Apple Services ID.
 */
private suspend fun adminOauthLogin(
    provider: String,
    idToken: String,
    fullName: String?,
): Boolean? {
    val body = json("idToken" to idToken)
    if (!fullName.isNullOrBlank()) body["fullName"] = fullName
    val response =
        runCatching {
            window.fetch(
                AppConfig.getApiUrl("v1/auth/oauth/$provider"),
                RequestInit(
                    method = "POST",
                    headers = json("Content-Type" to "application/json"),
                    body = JSON.stringify(body),
                    credentials = INCLUDE_CREDENTIALS,
                ),
            ).await()
        }.getOrNull() ?: return null
    if (!response.ok) return null
    val text = response.text().await()
    val access =
        runCatching { JSON.parse<dynamic>(text).data.accessToken as? String }.getOrNull()
    if (access.isNullOrBlank()) return null
    Session.accessToken = access
    runCatching { JSON.parse<dynamic>(text).data.refreshToken as? String }.getOrNull()
        ?.let { Session.refreshToken = it }
    return runCatching { JSON.parse<dynamic>(text).data.isNewUser as? Boolean }.getOrNull() ?: false
}

/**
 * Sign-in / sign-up in one screen: Google or Apple only, run inline on the admin's own
 * origin (Google Identity Services button + Sign in with Apple). On success the token
 * pair is stored in [Session] and the panel boots; owners with no venue then land in the
 * creation wizard.
 */
@Composable
fun LoginView(
    notice: String? = null,
    onLoggedIn: () -> Unit,
) {
    val scope = rememberCoroutineScope()
    var busy by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }

    fun finish(provider: String, idToken: String, fullName: String?) {
        busy = true
        error = null
        scope.launch {
            val isNewUser = adminOauthLogin(provider, idToken, fullName)
            busy = false
            if (isNewUser != null) {
                if (isNewUser) Analytics.signUp(method = provider)
                onLoggedIn()
            } else {
                error = AdminT.authLoginFailed
            }
        }
    }

    // Render Google's real (invisible) button + warm the Apple SDK on mount. Re-render
    // the Google button whenever the window regains focus: if the user opened the Google
    // flow and backed out, GIS can leave the button in a stuck "in-progress" state, and
    // re-rendering it on return makes it clickable again.
    DisposableEffect(Unit) {
        fun renderGoogle() {
            OAuthHelper.signInWithGoogleButton(
                buttonElementId = ADMIN_GOOGLE_BUTTON_ID,
                onSuccess = { user -> finish("google", user.idToken, user.name) },
                onError = { error = AdminT.authLoginFailed },
            )
        }
        renderGoogle()
        OAuthHelper.loadAppleSDK { } // preload so the first Apple click opens instantly
        val onFocus: (Event) -> Unit = { renderGoogle() }
        window.addEventListener("focus", onFocus)
        onDispose { window.removeEventListener("focus", onFocus) }
    }

    AuthScaffold {
        AuthFormHeader(AdminT.authCabinet)

        notice?.let {
            Row(
                Modifier
                    .fillMaxWidth()
                    .backgroundColor(Brand.GreenSoft)
                    .borderRadius(12.px)
                    .padding(12.px)
                    .margin(bottom = 14.px),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                InlineIcon(AdminIcons.Users, 16, "#0F9D6E")
                SpanText(
                    it,
                    Modifier.fontSize(13.px).fontWeight(FontWeight.Medium).color(Brand.Green)
                        .margin(left = 8.px),
                )
            }
        }

        // Google: styled row with the real (invisible) GIS button stretched on top.
        Box(Modifier.fillMaxWidth().styleModifier { property("position", "relative") }) {
            Row(
                Modifier
                    .fillMaxWidth()
                    .height(46.px)
                    .backgroundColor(Brand.Surface)
                    .border(1.px, LineStyle.Solid, Brand.Divider)
                    .borderRadius(12.px),
                horizontalArrangement = Arrangement.Center,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                InlineIcon(MenuIcons.GoogleLogo, 18, "#1F2421")
                SpanText(
                    AdminT.authContinueGoogle,
                    Modifier.fontSize(14.px).fontWeight(FontWeight.SemiBold)
                        .color(Brand.Charcoal).margin(left = 9.px),
                )
            }
            Div(
                attrs = {
                    id(ADMIN_GOOGLE_BUTTON_ID)
                    style {
                        property("position", "absolute")
                        property("inset", "0")
                        property("display", "flex")
                        property("justify-content", "center")
                        property("overflow", "hidden")
                        property("opacity", "0.001")
                    }
                },
            )
        }

        // Apple: Sign in with Apple opens Apple's own native dialog.
        Row(
            Modifier
                .fillMaxWidth()
                .height(46.px)
                .backgroundColor(Brand.Charcoal)
                .borderRadius(12.px)
                .margin(top = 10.px)
                .cursor(Cursor.Pointer)
                .attrsModifier { classes("qk-press"); attr("role", "button") }
                .onClick {
                    if (!busy) {
                        OAuthHelper.signInWithApple(
                            onSuccess = { idToken, _, name -> finish("apple", idToken, name) },
                            onError = { error = AdminT.authLoginFailed },
                        )
                    }
                },
            horizontalArrangement = Arrangement.Center,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            InlineIcon(MenuIcons.AppleLogo, 18, "#FFFFFF")
            SpanText(
                AdminT.authContinueApple,
                Modifier.fontSize(14.px).fontWeight(FontWeight.SemiBold)
                    .color(Brand.Surface).margin(left = 9.px),
            )
        }

        AdminError(error)
    }
}

/** Site-address rules mirrored from the backend (Restaurant.SLUG_REGEX). */
internal val SLUG_REGEX = Regex("^[a-z0-9][a-z0-9-]{1,28}[a-z0-9]$")

/** Localizes venue-creation errors (site address taken/reserved/format, name length). */
internal fun mapSlugError(
    errors: List<String>?,
    fallback: String,
): String {
    val raw = errors?.firstOrNull() ?: return fallback
    return when {
        "already taken" in raw -> AdminT.authErrDomainTaken
        "reserved" in raw -> AdminT.authErrDomainReserved
        "Slug must be" in raw -> AdminT.authErrDomainRules
        "Name must be" in raw -> AdminT.authErrNameRules
        else -> fallback
    }
}

/**
 * Neutral boot screen on the emerald auth backdrop: logo + spinner, and an optional
 * message with a retry action when the boot request keeps failing. This is also what
 * the prerendered /admin snapshot shows, so a refresh never flashes the login card.
 */
@Composable
fun AdminBootSplash(
    message: String? = null,
    actionText: String? = null,
    onAction: (() -> Unit)? = null,
) {
    InjectAdminStyles()
    Box(
        Modifier.fillMaxWidth().attrsModifier { classes("qk-auth") },
        contentAlignment = Alignment.Center,
    ) {
        Column(horizontalAlignment = Alignment.CenterHorizontally) {
            Box(Modifier.margin(bottom = 18.px)) { AuthLogo(onDark = true) }
            if (message == null) {
                Box(Modifier.size(26.px).attrsModifier { classes("qk-spin") })
            } else {
                SpanText(
                    message,
                    Modifier
                        .fontSize(15.px)
                        .color(Color.rgba(255, 255, 255, 0.85f))
                        .margin(bottom = 18.px)
                        .attrsModifier { style { property("text-align", "center") } },
                )
                if (actionText != null && onAction != null) {
                    AdminButton(text = actionText, onClick = onAction)
                }
            }
        }
    }
}

/** Centered status card (accepting / joined / failed), reusing the branded frame. */
@Composable
internal fun AuthStatusView(
    icon: String?,
    title: String,
    tone: String,
    actionText: String? = null,
    onAction: (() -> Unit)? = null,
) {
    val (iconBg, iconColor) =
        when (tone) {
            "success" -> Brand.GreenSoft to "#0F9D6E"
            "danger" -> Brand.CoralSoft to "#E05B4C"
            else -> Brand.WarmBackground to "#6B7280"
        }
    AuthScaffold {
        Column(Modifier.fillMaxWidth(), horizontalAlignment = Alignment.CenterHorizontally) {
            if (icon != null) {
                Box(
                    Modifier.size(60.px).borderRadius(999.px).backgroundColor(iconBg)
                        .margin(top = 6.px, bottom = 16.px),
                    contentAlignment = Alignment.Center,
                ) {
                    InlineIcon(icon, 26, iconColor)
                }
            }
            SpanText(
                title,
                Modifier
                    .fillMaxWidth()
                    .fontSize(17.px)
                    .fontWeight(FontWeight.Bold)
                    .color(Brand.Charcoal)
                    .attrsModifier { style { property("text-align", "center") } },
            )
            if (actionText != null && onAction != null) {
                Row(
                    Modifier.fillMaxWidth().margin(top = 20.px),
                    horizontalArrangement = Arrangement.Center,
                ) {
                    AdminButton(text = actionText, onClick = onAction)
                }
            }
        }
    }
}
